'use client'

import { useEffect, useState } from 'react'
import {
  Button,
  Dialog,
  Navigation,
  Divider,
  Text,
  Container,
  Icon,
  Spacer,
  Cell,
} from '@vinted/web-ui'
import { InfoCircle16, Redirect16, X24 } from '@vinted/monochrome-icons'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { redirectToPageInNewTab } from '@marketplace-web/browser/browser-navigation-util'
import {
  ClickAdEventTarget,
  VanAdEligibleScreen,
  VanCreativeModel,
  VanPlacementConfigModel,
} from '@marketplace-web/ads/ads-data'

import AdInfo from '../../AdInfo'
import useAdEventTracking from '../../../../hooks/useAdEventTracking'
import useFetchVanCreative from '../../../../hooks/useFetchVanCreative'
import useClickAdEventTracking from '../../../../hooks/useClickAdEventTracking'
import { VAN_CAMPAIGN_PRIVACY_POLICY_URL, PRIVACY_POLICY_URL } from '../../../../constants/routes'

type Props = {
  isOpen: boolean
  screen: VanAdEligibleScreen
  config: VanPlacementConfigModel
  onClose: (target: ClickAdEventTarget) => void
  creative: VanCreativeModel
}

const IFRAME_BUTTON_CLOSE_DELAY = 100
const RETRY_INTERVAL_MS = 200

const VanOfferContent = ({ isOpen, screen, config, creative, onClose }: Props) => {
  const [isAdRendered, setIsAdRendered] = useState(false)

  const { viewableImpressionRef, impressionRef } = useAdEventTracking({
    screen,
    isAdRendered,
    placementConfig: config,
    customPlacementId: config.placementId,
  })
  const { trackClickAdEvent } = useClickAdEventTracking()

  const translate = useTranslate('vinted_ads_network.ad_offer')

  const { iframeContent } = useFetchVanCreative({ screen, config, creative })

  const onOfferButtonClick = () => {
    redirectToPageInNewTab(creative.clickThroughUrl)

    onClose(ClickAdEventTarget.Cta)
  }

  const onAdInfoClick = () => {
    if (!config?.id) return

    trackClickAdEvent({ placement: config, target: ClickAdEventTarget.AdInfo, screen })

    redirectToPageInNewTab(VAN_CAMPAIGN_PRIVACY_POLICY_URL(config.id))
  }

  const onPrivacyPolicyClick = () => {
    trackClickAdEvent({ placement: config, target: ClickAdEventTarget.PrivacyPolicy, screen })

    redirectToPageInNewTab(PRIVACY_POLICY_URL)
  }

  useEffect(() => {
    if (!iframeContent || !isOpen) return

    setIsAdRendered(true)
  }, [iframeContent, isOpen])

  // TODO: if this works can be moved to separate hook
  useEffect(() => {
    // If there is no iframeContent there is no point attaching the listener
    if (!iframeContent) return undefined

    let timeoutId: NodeJS.Timeout | null = null

    const clearExistingTimeout = () => {
      if (!timeoutId) return
      clearTimeout(timeoutId)
      timeoutId = null
    }

    const tryAttachListener = () => {
      const iframe = document.getElementById('van-offer-iframe') as HTMLIFrameElement | null

      if (!iframe || !iframe.contentWindow) {
        // Adds this on close delay to ensure that iframe button redirects user
        // to the correct URL before the dialog closes
        timeoutId = setTimeout(tryAttachListener, RETRY_INTERVAL_MS)

        return
      }

      const iframeImage = iframe.contentWindow.document.getElementById('van-creative')

      if (!iframeImage) {
        timeoutId = setTimeout(tryAttachListener, RETRY_INTERVAL_MS)

        return
      }

      iframeImage.addEventListener('click', () => {
        setTimeout(() => {
          onClose(ClickAdEventTarget.Creative)
        }, IFRAME_BUTTON_CLOSE_DELAY)
      })

      clearExistingTimeout()
    }

    tryAttachListener()

    return clearExistingTimeout
  }, [iframeContent, onClose])

  if (!iframeContent || !isOpen || !config?.id) return null

  return (
    <Dialog
      show={isOpen}
      closeOnOverlay
      defaultCallback={() => onClose(ClickAdEventTarget.CloseClickOutsidePlacement)}
    >
      <div ref={viewableImpressionRef}>
        <div ref={impressionRef}>
          <Navigation
            body={translate('header_title')}
            right={
              <Button
                inline
                iconName={X24}
                iconColor="greyscale-level-2"
                styling="flat"
                testId="van-offer-navigation-close"
                aria={{ 'aria-label': translate('a11y.actions.close') }}
                onClick={() => onClose(ClickAdEventTarget.CloseButton)}
              />
            }
          />
          <Divider />
          <Container>
            <div className="van-offer-container">
              <div data-testid="van-offer-slot" id="van-offer-slot">
                <iframe
                  id="van-offer-iframe"
                  title="van-offer-iframe"
                  width={creative.size.width}
                  height={creative.size.height}
                  className="van-offer-iframe"
                  srcDoc={iframeContent}
                />
              </div>
              <Spacer size="regular" />
              <div style={{ width: creative.size.width }}>
                <Text
                  as="h2"
                  type="section-heading"
                  text={translate('title')}
                  alignment="center"
                  width="parent"
                />
                <Text
                  as="p"
                  text={translate('subtitle')}
                  type="subtitle"
                  alignment="center"
                  width="parent"
                />
                <Spacer size="medium" />
                <Button
                  text={translate('button')}
                  iconPosition="right"
                  iconName={Redirect16}
                  styling="filled"
                  size="medium"
                  theme="primary"
                  onClick={onOfferButtonClick}
                  testId="van-offer-claim-button"
                />
              </div>
            </div>
          </Container>
          <Container>
            <div className="van-offer-info-container">
              <div>
                <Cell
                  styling="tight"
                  clickable
                  body={
                    <div className="van-offer-info">
                      <AdInfo />
                      <Icon name={InfoCircle16} color="greyscale-level-2" />
                    </div>
                  }
                  onClick={onAdInfoClick}
                  testId="ad-info-redirection-button"
                />
              </div>
              <div>
                <Cell
                  styling="tight"
                  clickable
                  body={
                    <div className="van-offer-info">
                      <Text
                        as="p"
                        type="caption"
                        theme="muted"
                        text={translate('privacy_policy')}
                        underline
                      />
                    </div>
                  }
                  onClick={onPrivacyPolicyClick}
                  testId="privacy-policy-redirection-button"
                />
              </div>
            </div>
          </Container>
        </div>
      </div>
    </Dialog>
  )
}

export default VanOfferContent
